import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import {
    fetchPages,
    fetchTemplates,
    updatePage,
    selectAllPages,
    selectTemplates,
    selectPageStatus,
    selectPageSaving,
    selectPageError,
} from './PageSlice';
import { useIsMobile } from '../../theme';

// What each `kind` means, shown as a hint in the list so it is obvious why some
// pages have no article type.
const KIND_LABEL = {
    type: 'Listagem (tipo de artigo)',
    home: 'Início (todos os tipos)',
    static: 'Página estática',
    search: 'Resultados de pesquisa',
};

// Site defaults, mirroring TEMPLATE_SLOTS in the renderer — shown as the
// "inherit" option so it is clear what an empty slot actually renders with.
// `group` is the src/templates/ folder the slot's options come from; each
// dropdown lists only that folder, so a card can't be picked as a page body.
const TEMPLATE_SLOTS = {
    template: { default: 'main', group: 'pages' },
    cardTemplate: { default: 'card', group: 'cards' },
    articleTemplate: { default: 'article', group: 'articles' },
};

// A template picker. An empty value means "use the site default" (stored NULL).
// `InputLabelProps.shrink` is required, not cosmetic: the default value is '',
// so MUI treats the field as empty and parks the label over the input — but a
// native <select> always paints its selected option, and the two overlap.
const TemplateSelect = ({ label, field, value, templates, helperText, onChange }) => {
    const { default: fallback, group } = TEMPLATE_SLOTS[field];
    const options = (templates && templates[group]) || [];

    return (
        <TextField
            select
            label={label}
            value={value}
            onChange={onChange}
            helperText={`${helperText} — de src/templates/${group}/`}
            fullWidth
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
        >
            <option value=''>— predefinido ({fallback}) —</option>
            {options.map((name) => (
                <option key={name} value={name}>
                    {name}
                </option>
            ))}
        </TextField>
    );
};

// #region EDIT DIALOG
const PageEditDialog = ({ page, open, saving, templates, onClose, onSave }) => {
    const isMobile = useIsMobile();
    const [form, setForm] = useState(null);

    // Reload the form whenever a different page is opened.
    useEffect(() => {
        if (!page) return setForm(null);
        setForm({
            headTitle: page.headTitle || '',
            headDescription: page.headDescription || '',
            headerTitle: page.headerTitle || '',
            navLabel: page.navLabel || '',
            template: page.template || '',
            cardTemplate: page.cardTemplate || '',
            articleTemplate: page.articleTemplate || '',
            isVisible: !!page.isVisible,
        });
    }, [page]);

    if (!page || !form) return null;

    const setField = (field) => (event) => {
        const value = field === 'isVisible' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // Send only what actually changed, so an untouched field is never written.
    const handleSave = () => {
        const changes = {};
        if (form.headTitle !== (page.headTitle || '')) changes.headTitle = form.headTitle;
        if (form.headDescription !== (page.headDescription || '')) changes.headDescription = form.headDescription;
        if (form.headerTitle !== (page.headerTitle || '')) changes.headerTitle = form.headerTitle;
        if (form.navLabel !== (page.navLabel || '')) changes.navLabel = form.navLabel;
        if (form.template !== (page.template || '')) changes.template = form.template;
        if (form.cardTemplate !== (page.cardTemplate || '')) changes.cardTemplate = form.cardTemplate;
        if (form.articleTemplate !== (page.articleTemplate || '')) changes.articleTemplate = form.articleTemplate;
        if (form.isVisible !== !!page.isVisible) changes.isVisible = form.isVisible;

        if (!Object.keys(changes).length) return onClose();
        onSave(page.idPage, changes);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ pb: 1 }}>
                {page.filename}
                <Typography variant='caption' color='text.secondary' component='div'>
                    {KIND_LABEL[page.kind] || page.kind}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label='Título (head)'
                        helperText='Separador do browser e título nos resultados de pesquisa (<title>)'
                        value={form.headTitle}
                        onChange={setField('headTitle')}
                        fullWidth
                    />
                    <TextField
                        label='Meta descrição'
                        helperText='O excerto <meta name="description"> mostrado pelos motores de busca'
                        value={form.headDescription}
                        onChange={setField('headDescription')}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <TextField
                        label='Título do cabeçalho'
                        helperText='A marca mostrada no cabeçalho da página'
                        value={form.headerTitle}
                        onChange={setField('headerTitle')}
                        fullWidth
                    />
                    <TextField
                        label='Etiqueta no menu'
                        helperText={
                            page.kind === 'type'
                                ? `Deixe vazio para usar o nome do tipo de artigo (“${page.displayName || ''}”)`
                                : 'Texto do link desta página no menu de topo'
                        }
                        value={form.navLabel}
                        onChange={setField('navLabel')}
                        fullWidth
                    />

                    <Divider />
                    <Typography variant='caption' color='text.secondary'>
                        Templates — cada campo lista os ficheiros da sua própria pasta em <code>src/templates/</code>. Deixe em “predefinido”
                        para herdar o do site.
                    </Typography>
                    <TemplateSelect
                        label='Template da página'
                        field='template'
                        value={form.template}
                        templates={templates}
                        helperText='Desenha o corpo desta página'
                        onChange={setField('template')}
                    />
                    {/* Cards and article pages only exist for feeds. */}
                    {(page.kind === 'type' || page.kind === 'home') && (
                        <TemplateSelect
                            label='Template do cartão'
                            field='cardTemplate'
                            value={form.cardTemplate}
                            templates={templates}
                            helperText='Um item da listagem — usado onde quer que este tipo apareça (início, pesquisa)'
                            onChange={setField('cardTemplate')}
                        />
                    )}
                    {page.kind === 'type' && (
                        <TemplateSelect
                            label='Template do artigo'
                            field='articleTemplate'
                            value={form.articleTemplate}
                            templates={templates}
                            helperText='Página de detalhe de um artigo deste tipo'
                            onChange={setField('articleTemplate')}
                        />
                    )}

                    <Divider />
                    <FormControlLabel
                        control={<Switch checked={form.isVisible} onChange={setField('isVisible')} />}
                        label='Visível (publicada no site e ligada no menu)'
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <Button onClick={onClose} disabled={saving} color='inherit'>
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant='contained' disabled={saving}>
                    {saving ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// One page in the list. Same card at every width — the old six-column grid
// needed a horizontal scrollbar on anything narrower than a laptop.
const PageRow = ({ page, onEdit }) => (
    <Paper
        variant='outlined'
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: { xs: 1, md: 1.5 },
            borderLeft: 4,
            borderLeftColor: page.isVisible ? 'success.main' : 'divider',
        }}
    >
        <Box
            onClick={() => onEdit(page)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit(page)}
            sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>{page.filename}</Typography>
                <Chip
                    size='small'
                    variant='outlined'
                    color={page.isVisible ? 'success' : 'default'}
                    label={page.isVisible ? 'NO SITE' : 'ESCONDIDA'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                />
                {page.showInNav && (
                    <Chip
                        size='small'
                        variant='outlined'
                        label={`menu: ${page.navLabel || page.displayName || ''}`}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                )}
            </Box>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.25 }}>
                {KIND_LABEL[page.kind] || page.kind}
            </Typography>
            {page.headTitle && (
                <Typography variant='body2' color='text.secondary' noWrap sx={{ mt: 0.25 }}>
                    {page.headTitle}
                </Typography>
            )}
            <Tooltip title='página / cartão / artigo'>
                <Typography variant='caption' color='text.disabled' component='div'>
                    {[page.template || 'main', page.cardTemplate || 'card', page.articleTemplate || 'article'].join(' / ')}
                </Typography>
            </Tooltip>
        </Box>
        <IconButton color='primary' onClick={() => onEdit(page)} aria-label='Editar página'>
            <EditIcon />
        </IconButton>
    </Paper>
);

// #region PAGES
const Pages = () => {
    const dispatch = useDispatch();

    const pages = useSelector(selectAllPages);
    const templates = useSelector(selectTemplates);
    const status = useSelector(selectPageStatus);
    const saving = useSelector(selectPageSaving);
    const error = useSelector(selectPageError);

    const [editing, setEditing] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(fetchPages());
        dispatch(fetchTemplates());
    }, [dispatch]);

    useEffect(() => {
        if (error) setSnackbar({ open: true, message: error, severity: 'error' });
    }, [error]);

    const handleSave = useCallback(
        async (id, changes) => {
            const result = await dispatch(updatePage({ id, changes }));
            if (updatePage.fulfilled.match(result)) {
                setEditing(null);
                // Visibility and template changes reach other pages (the nav and
                // this type's cards on the home feed), so those rebuild everything.
                const siteWide = ['isVisible', 'template', 'cardTemplate', 'articleTemplate'].some((f) => changes[f] !== undefined);
                setSnackbar({
                    open: true,
                    message: siteWide ? 'Página guardada — site inteiro reconstruído' : 'Página guardada e reconstruída',
                    severity: 'success',
                });
            }
        },
        [dispatch],
    );

    const rows = useMemo(() => pages || [], [pages]);

    // #region RETURN
    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 }, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                    Texto de cabeçalho e meta de cada página pública. Guardar reconstrói o HTML afetado de imediato — sem reinício. O nome do
                    ficheiro e o tipo são estruturais e definidos por migração.
                </Typography>
                {status === 'loading' && <CircularProgress size={14} />}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
                {rows.map((page) => (
                    <PageRow key={page.idPage} page={page} onEdit={setEditing} />
                ))}
                {!rows.length && status !== 'loading' && (
                    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color='text.secondary'>Sem páginas.</Typography>
                    </Paper>
                )}
            </Box>

            <PageEditDialog
                page={editing}
                open={!!editing}
                saving={saving}
                templates={templates}
                onClose={() => setEditing(null)}
                onSave={handleSave}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Pages;
